import assign from 'lodash/assign';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import find from 'lodash/find';
import flow from 'lodash/flow';
import getByPath from 'lodash/get';
import isInteger from 'lodash/isInteger';
import map from 'lodash/map';
import pick from 'lodash/pick';
import startsWith from 'lodash/startsWith';

import uuid from 'uuid-v4';
import moment from 'moment';
import ngCookies from 'angular-cookies';

import { AppSettingsModule } from 'services/settings.provider';
import { NotificationServiceModule } from 'services/utilities/notification.service';
import { StorageServiceModule } from 'services/storage/storage.service';
import { AuthTokenServiceModule } from 'services/auth/authToken.service';
import { DiscountsServiceModule } from 'services/data/discounts.service';
import { DiscountsRESTServiceModule } from 'services/data/discountsREST.service';
import { LaunchDarklyServiceModule } from 'services/access/launchDarkly/launchDarkly.service';

import { DataServiceModule } from './data.service';
import { ModelServiceModule } from './model/model.service';
import { PaypalServiceModule } from '../payment/paypal/paypal.service';
import { SearchServiceModule } from './search.service';
import { SegmentServiceModule } from '../tracking/segment/segment.service';
import { StoreSettingsServiceModule } from './storeSettings.service';
import orderConstants from '../../pages/orders/orderConstants';

export const OrderServiceModule = angular
    .module('app.services.data.orderService', [
        'ngMaterial',
        ngCookies,
        DataServiceModule,
        AppSettingsModule,
        ModelServiceModule,
        StorageServiceModule,
        SearchServiceModule,
        SegmentServiceModule,
        NotificationServiceModule,
        StoreSettingsServiceModule,
        PaypalServiceModule,
        AuthTokenServiceModule,
        DiscountsServiceModule,
        DiscountsRESTServiceModule,
        LaunchDarklyServiceModule
    ]).factory('orderService', (
        $q,
        $interval,
        $rootScope,
        $window,
        $cookies,
        $translate,
        $mdDialog,
        dataService,
        settings,
        modelService,
        notificationService,
        paypalService,
        searchService,
        segmentService,
        storageService,
        storeSettingsService,
        authTokenService,
        discountsService,
        discountsRESTService,
        launchDarklyService
    ) => {
        var ORDER_STATE = {
            all: 'all',
            new: 'New',
            pending: 'Pending',
            inProgress: 'In Progress',
            completed: 'Complete',
            canceled: 'Canceled',
            archived: 'Archived',
            inbox: 'Inbox',
            refunded: 'Refunded',
            flagged: 'Flagged'
        };
        const _archivedFilter = `status eq "${ORDER_STATE.completed}"
                        or status eq "${ORDER_STATE.canceled}"
                        or status eq "${ORDER_STATE.refunded}"`;
        const _newStatusFilter = `status eq "${ORDER_STATE.new}"`;
        const _inProgressFilter = `status eq "${ORDER_STATE.inProgress}" or status eq "${ORDER_STATE.pending}"`;
        const _flaggedFilter = `verification.isFlaggedForReview eq "true" and ${_newStatusFilter}`;
        const _inboxFilter = `${_newStatusFilter}
                        or status eq "${ORDER_STATE.inProgress}"
                        or status eq "${ORDER_STATE.pending}"`;
        const CUSTOM = orderConstants.DISCOUNT_TYPE.CUSTOM;
        const FIXED = orderConstants.DISCOUNT_TYPE.FIXED;
        const PERCENT = orderConstants.DISCOUNT_TYPE.PERCENT;
        const MONGO_ERROR = 'Document not found for filter: ';

        return {
            basicDialogController,
            buildFilter,
            bulkAction,
            bulkUpdate,
            bulkUpload,
            create,
            createOrUpdateCustomDiscount,
            createPaymentPromises,
            editFulfillmentEvent,
            get,
            getAdjustedGrandTotal,
            getAdjustmentAmount,
            getBulkUpdateStatus,
            getCartImages,
            getCombinedItemsDiscount,
            getCustomerByEmail,
            getCustomerById,
            getCustomerOrders,
            getDiscountDisplayLines,
            getDiscountedItemTotals,
            getErrItem,
            getErrorMessage,
            getHistory,
            getItemQuantity,
            getItems,
            getMetrics,
            getNextOrderId,
            getOrderCount,
            getOrderHistory,
            getOriginalAmount,
            getPreviousOrderId,
            getProductInventory,
            getQuantityMoreAvailable,
            getRequestId,
            getShippingMethods,
            handleTaxProviderMessageEvent,
            hasDiscountedPrices,
            initOrderNotify,
            isFixedCustomDiscount,
            isFullyRefunded,
            isInvalidQuantity,
            isInventoryTracked,
            isPercentCustomDiscount,
            isPrematureRefundError,
            isQuantityExceedingInventory,
            isRefundWaitingPeriodPassed,
            isRegularDiscount,
            isTestOrder,
            isTrackingRequired,
            logFulfillmentEvent,
            markShipped,
            mergeOrder,
            parseCartError,
            readyPaymentData,
            recalculateTax,
            refreshOrder,
            resendOrderConfirmationEmail,
            resendShippingConfirmationEmail,
            save,
            sendPaymentEvent,
            shouldShowOrderShipping,
            showErrorModal,
            tabulateAndCreateCustomDiscount,
            updateDiscountedPrices,
            updateOrderFn
        };

        function buildFilter(filterQuery) {
            var filter;

            switch (filterQuery) {
                case ORDER_STATE.archived:
                    filter = _archivedFilter;
                    break;
                case ORDER_STATE.inbox:
                    filter = _inboxFilter;
                    break;
                case ORDER_STATE.inProgress:
                    filter = _inProgressFilter;
                    break;
                case ORDER_STATE.flagged:
                    filter = _flaggedFilter;
                    break;
                default:
                    filter = `status eq "${filterQuery}"`;
                    break;
            }

            return filter;
        }

        function bulkUpdate(csvObj) {
            return dataService.saveAll(settings.api.orderBulkUpdate, csvObj);
        }

        function getBulkUpdateStatus(importerJobId) {
            return dataService.get(settings.api.orderBulkUpdateStatus, importerJobId);
        }

        function bulkUpload(csvObj) {
            return dataService.saveAll(settings.api.orderBulkUpload, csvObj);
        }

        function bulkAction(type, orders, self) {
            if (type === 'accept') {
                const bulkPaymentPromises = createPaymentPromises(orders, type, self);
                return $q.all(bulkPaymentPromises);
            }
        }

        function createPaymentPromises(orders, type, self) {
            return orders.map((order) => {
                const data = readyPaymentData(order);

                return sendPaymentEvent(order.id, data)
                    .then(() => {
                        self.$window.dataLayer.push({
                            event: `admin_order.detail_${type}-acceptPayment`
                        });
                        return get(order.id);
                    })
                    .then((response) => {
                        if (shouldShowOrderShipping(response)) {
                            return getOrderHistory(response);
                        }
                        const carrierInfo = [{ carrier: orderConstants.NO_SHIPPING, number: '' }];
                        return markShipped(response, carrierInfo, 'action', self);
                    })
                    .catch(err => $q.reject(err));
            });
        }

        function readyPaymentData(order) {
            const data = { eventType: 'charge' };
            const paypalTransaction = order.payPalPaymentTransactionId;
            if (paypalTransaction) {
                return Object.assign(
                    {},
                    data,
                    { payPalPaymentTransactionId: paypalTransaction }
                );
            }
            return data;
        }

        function shouldShowOrderShipping(order) {
            return order.cart.items.reduce((acc, item) => {
                return acc || (
                    item.product.requiresShipping === undefined || // no prop == default to true
                    item.product.requiresShipping === true
                );
            }, false);
        }

        // Returns processed order object
        function updateOrderFn() {
            return flow([
                addOrderDiffItems,
                addPaymentLifeCycleEvents,
                addPaymentMethodEvents,
                addShippingLabelEvents
            ]);
        }

        function getOrderHistory(order) {
            order.history = order.history || [];
            order.history.push(initOrderHistory(order));

            return getHistory(order.auditLogId)
                .then((response) => {
                    const updateOrder = updateOrderFn();
                    const clonedOrder = cloneDeep(order);
                    return updateOrder(response.auditItems, clonedOrder);
                });
        }

        function initOrderHistory(order) {
            return {
                icon: 'shopping_cart',
                text: 'ORDER.PLACED',
                date: order.createdOn
            };
        }

        function addOrderDiffItems(auditItems, order) {
            each(auditItems, (item) => {
                each(item.differences, (diff) => {
                    if (diff.fieldName.includes('PlacedBy') || diff.fieldName.includes('DeliverTo')) {
                        order.history.push({
                            icon: 'mode_edit',
                            text: 'ORDER.ORDEREDITED',
                            date: item.createdOn
                        });
                    }

                    switch (diff.fieldName) {
                        case 'Paid':
                            if (diff.modifiedValue === 'True') {
                                order.history.push({
                                    icon: 'credit_card',
                                    text: 'ORDER.PAYMENTACCEPTED',
                                    date: item.createdOn
                                });
                                order.paymentDate = moment(item.createdOn).format('MM/DD/YYYY');
                            }

                            break;
                        case 'Status':
                            if (diff.modifiedValue === 'Complete') {
                                order.history.push({
                                    icon: 'check',
                                    text: 'ORDER.COMPLETED',
                                    date: item.createdOn
                                });
                                if (order.shipped) {
                                    order.history.push({
                                        icon: 'mail',
                                        text: 'ORDER.SHIPPED',
                                        date: item.createdOn
                                    });
                                    order.shippedDate = item.createdOn;
                                }
                            } else if (diff.modifiedValue === 'Canceled') {
                                order.history.push({
                                    icon: 'clear',
                                    text: 'ORDER.CANCELED',
                                    date: item.createdOn
                                });
                                order.cancelDate = moment(item.createdOn).format('MM/DD/YYYY');
                            }

                            break;
                        case 'MerchantNote':
                            if (diff.modifiedValue !== '' &&
                                diff.modifiedValue !== '(null)' &&
                                (diff.originalValue === '' || diff.originalValue === '(null)')) {
                                order.history.push({
                                    icon: 'speaker_notes',
                                    text: 'ORDER.NOTEADDED',
                                    date: item.createdOn
                                });
                            } else if (diff.modifiedValue !== '' &&
                                diff.modifiedValue !== '(null)' &&
                                diff.originalValue !== '' &&
                                diff.originalValue !== '(null)') {
                                order.history.push({
                                    icon: 'speaker_notes',
                                    text: 'ORDER.NOTEUPDATED',
                                    date: item.createdOn
                                });
                            } else if (diff.originalValue !== '' &&
                                diff.originalValue !== '(null)' &&
                                (diff.modifiedValue === '' ||
                                diff.modifiedValue === '(null)')) {
                                order.history.push({
                                    icon: 'speaker_notes',
                                    text: 'ORDER.NOTEREMOVED',
                                    date: item.createdOn
                                });
                            }

                            break;
                        // no default
                    }
                });
            });
            return order;
        }

        function addPaymentLifeCycleEvents(order) {
            const { paymentLifecycle } = order;
            each(paymentLifecycle, (event) => {
                switch (event.eventType.toLowerCase()) {
                    case 'refund':
                        order.history.push({
                            icon: 'refresh',
                            text: 'ORDER.REFUNDED',
                            date: event.createdOn
                        });
                        order.refundDate = moment(event.createdOn).format('MM/DD/YYYY');
                        break;
                        // no default
                }
            });
            return order;
        }

        function addPaymentMethodEvents(order) {
            // Create one array of payment events
            const paymentEvents = [
                ...(order.payPalEvents ? order.payPalEvents : []),
                ...(order.stripeEvents ? order.stripeEvents : [])
            ];

            each(paymentEvents, (event) => {
                switch (event.type) {
                    case 'CapturedChargeSuccessful':
                        order.history.push({
                            icon: 'check',
                            text: 'ORDER.CHARGESUCCESSFUL',
                            date: event.timestamp
                        });
                        break;
                    case 'CapturedChargeError':
                        order.history.push({
                            icon: 'close',
                            text: 'ORDER.CHARGEERROR',
                            date: event.timestamp
                        });

                        order.chargeError = event.description;
                        break;
                    case 'ChargeRefundSuccessful':
                        order.history.push({
                            icon: 'refresh',
                            text: 'ORDER.REFUNDSUCCESSFUL',
                            date: event.timestamp
                        });
                        break;
                    case 'ChargeRefundError':
                        order.history.push({
                            icon: 'close',
                            text: 'ORDER.REFUNDERROR',
                            date: event.timestamp
                        });
                        break;
                    case 'CanceledReversalSuccessful':
                        order.history.push({
                            icon: 'credit_card',
                            text: 'ORDER.REVERSECANCELED',
                            date: event.timestamp
                        });
                        break;
                    // no default
                }
            });
            return order;
        }

        function addShippingLabelEvents(order) {
            each(order.fulfillmentLifecycle, (event) => {
                switch (event.eventType) {
                    case 'PostagePaid':
                        order.history.push({
                            svgSrc: '/assets/shippo-icon.svg',
                            text: 'POSTAGE.EVENT_LABELPURCHASED',
                            date: event.createdOn
                        });
                        break;
                    case 'PostageRefunded':
                        order.history.push({
                            svgSrc: '/assets/shippo-icon.svg',
                            text: 'POSTAGE.EVENT_LABELREFUNDED',
                            date: event.createdOn
                        });
                        break;
                    // no default
                }
            });
            return order;
        }

        function markShipped(order, trackingInfo, clickType, self) {
            return logFulfillmentEvent(order.id, {
                eventType: 'shipped',
                trackingInformation: trackingInfo
            }).then(() => {
                self.$window.dataLayer.push({
                    event: `admin_order.detail_${clickType}-markShipped`
                });
                return refreshOrder(order)
                    .then(refreshedOrder => refreshedOrder);
            });
        }

        function refreshOrder(order) {
            return get(order.id).then((response) => {
                return getHistorySetShipping(response);
            });
        }

        function getHistorySetShipping(order) {
            return getOrderHistory(order)
                .then(setTrackingNumbers)
                .then(setShippingMethodAndStoreInfo);
        }

        function setTrackingNumbers(order) {
            const { trackingInformation } = order;
            order.trackingElements = map(trackingInformation, (trackingElement, index) => {
                let linkUrl = trackingElement.carrier === 'USPS'
                    ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingElement.number}`
                    : `//google.com/search?q=${trackingElement.number}`;
                return `<a href="${linkUrl}" target="_blank" class="text__link">
                ${trackingElement.number}
                </a>${(index === trackingInformation.length - 1) ? '.' : ''}`;
            });

            order.shippingCarrier = (trackingInformation && trackingInformation.length)
                ? trackingInformation[0].carrier
                : null;

            // Wrap to continue using the promise chain with `getHistorySetShipping`
            return $q.resolve(order);
        }

        function setShippingMethodAndStoreInfo(order) {
            return storeSettingsService.get().then((res) => {
                if (res.shippingMethods) {
                    order.shippingMethod = res.shippingMethods.name;
                }

                if (res.storeInformation) {
                    const storeInfo = modelService.makeModel(res.storeInformation, 'storeInformationModel');
                    paypalInit(storeInfo);
                }

                // Wrap to continue using the promise chain within `getHistorySetShipping`
                return $q.resolve(order);
            });
        }

        function paypalInit(storeInfo) {
            const paypalConnected = paypalService.isConnectedComplete(storeInfo);

            if (paypalConnected) {
                rectifyPaypalConnectionData(storeInfo);
            }
            if (!paypalConnected) {
                paypalService.prefill(true).then((response) => {
                    // eslint-disable-next-line no-console
                    console.log('response', response);
                });
            }
        }

        function rectifyPaypalConnectionData(storeInfo) {
            if (!storeInfo.paypal.permissionsGranted
                && paypalService.hasConnectedPaypalPermissions(storeInfo)) {
                paypalService.setPaypalConnected(storeInfo)
                    .then((response) => {
                        // This is a holdover from the orderDetails logic port
                        storeInfo = modelService.makeModel(response, 'storeInformationModel');
                    });
            }
        }

        function getHistory(id) {
            return dataService.get(settings.api.auditlogs, id);
        }

        function get(orderId) {
            return dataService.get(settings.api.orders, orderId);
        }

        function getItems(options = null) {
            return dataService.getAll(settings.api.orders, options);
        }

        function create(order) {
            return dataService.create(settings.api.orders, order);
        }

        function save(order) {
            return dataService.save(settings.api.orders, order.id, order);
        }

        function recalculateTax(order) {
            return dataService.get(`${settings.api.orders}/${order.id}/taxes`);
        }

        function getOrderCount(filter, sort) {
            var options = {
                filter: filter,
                pageSize: 1,
                sort: sort ? sort.replace(' ', '+') : null
            };

            return dataService.getAll(settings.api.orders, options)
                .then(response => response.totalCount);
        }

        function getMetrics() {
            const getAllMetrics = dataService.getAll(settings.api.ordersMetrics);
            return getAllMetrics.then((response) => {
                return {
                    flaggedCount: response.flaggedNewOrderCount,
                    inProgressCount: response.inProgressOrderCount,
                    newCount: response.newOrderCount,
                    newTotal: response.newOrderGrandTotalSum,
                    recentCount: response.recentOrderCount
                };
            });
        }

        function getCustomerOrders(customerId, page, count, sort) {
            var options = {
                filter: `customerId eq ${customerId}`,
                pageSize: count,
                startIndex: (page - 1) * count,
                sortBy: sort ? sort.replace(' ', '+') : null
            };

            return dataService.getAll(settings.api.orders, options);
        }

        function sendPaymentEvent(id, paymentObj) {
            return dataService.create(`${settings.api.orders}/${id}/paymentlifecycleevent`, paymentObj);
        }

        function logFulfillmentEvent(orderId, eventObject) {
            return dataService.create(`${settings.api.orders}/${orderId}/fulfillmentlifecycleevent`, eventObject);
        }

        function editFulfillmentEvent(orderId, fulfillmentId, eventObject) {
            const endpoint = `${settings.api.orders}/${orderId}/fulfillmentlifecycleevent`;
            return dataService.save(endpoint, fulfillmentId, eventObject);
        }

        function getShippingMethods() {
            // TODO get methods from API
            const SHIP = orderConstants.SHIPPING_METHODS;
            return [SHIP.UPS, SHIP.FEDEX, SHIP.USPS, SHIP.DHL, SHIP.PICKUP];
        }

        function isTrackingRequired(method) {
            return !!(method !== orderConstants.SHIPPING_METHODS.PICKUP);
        }

        function initOrderNotify() {
            authTokenService.getHeaders().then(({ token, tenantId }) => {
                if (tenantId && token) {
                    let notificationWorker = new $window.Worker('webWorkers/orderNotification.worker.js');
                    notificationWorker.postMessage({
                        tenant: tenantId,
                        token,
                        orderEndpoint: `${process.env.BASE_API_URL}/${settings.api.orders}`,
                        storeEndpoint: `${process.env.BASE_API_URL}/${settings.api.storeInformation}`
                    });

                    notificationWorker.onmessage = (e) => {
                        switch (e.data.message) {
                            case 'NOTIFY':
                                notificationService.createOrderNotification(e.data.order, e.data.storeName);
                                break;
                            //no default
                        }

                        $rootScope.$broadcast('orders:updateCount', e.data.totalCount);
                    };
                }
            });
        }

        function getCartImages(cartItems) {
            return map(cartItems, (item) => {
                return {
                    image: item.product.imageMetadata ? item.product.imageMetadata.imageLink.fullUri : '',
                    badge: item.quantity > 1 ? item.quantity : ''
                };
            });
        }

        function getCustomerByEmail(email) {
            var options = {
                pageSize: 1,
                filter: `emailAddress eq ${email}`
            };

            return dataService.getAll(settings.api.customers, options).then((response) => {
                if (response.items.length) {
                    return response.items[0];
                }
            });
        }

        function getCustomerById(id) {
            return dataService.get(settings.api.customers, id)
                .then(response => response);
        }

        function isRegularDiscount(order) {
            const discountName = getByPath(order, 'cart.discount.name', '');
            return discountName !== '' && discountName.toUpperCase() !== CUSTOM.toUpperCase();
        }

        function isFixedCustomDiscount(order) {
            const discountName = getByPath(order, 'cart.discount.name', '');
            const discountType = getByPath(order, 'cart.discount.discountType', '');
            return discountName.toUpperCase() === CUSTOM.toUpperCase() &&
                   discountType.toUpperCase() === FIXED.toUpperCase();
        }

        function isPercentCustomDiscount(order) {
            const discountName = getByPath(order, 'cart.discount.name', '');
            const discountType = getByPath(order, 'cart.discount.discountType', '');
            return discountName.toUpperCase() === CUSTOM.toUpperCase() &&
                discountType.toUpperCase() === PERCENT.toUpperCase();
        }

        function getDiscountDisplayLines(order) {
            const discounts = getByPath(order, 'cart.discounts', []);
            return discounts.map(({ discount, discountAmount }) => {
                const isCustom = discount.name.toUpperCase() === CUSTOM.toUpperCase();
                const isFixed = discount.discountType.toUpperCase() === FIXED.toUpperCase();
                const isAutomatic = !discount.code;
                const displayName = isCustom
                    ? CUSTOM.toUpperCase()
                    : (discount.code && discount.code.toUpperCase()) || 'AUTOMATIC';
                const discountValue = isFixed
                    ? `$${discount.fixedPriceReduction.toFixed(2)}`
                    : `${discount.percentagePriceReduction}%`;
                return {
                    displayName: `${displayName}${isCustom && !isFixed ? ` (${discountValue})` : ''}`,
                    discountCode: discount.code,
                    isAutomatic,
                    discountValue,
                    discountAmount
                };
            });
        }

        function getCombinedItemsDiscount(cartItems, itemsDiscountedPrices) {
            const cartTotal = cartItems.reduce((accumulator, currentValue) => {
                return accumulator + (currentValue.quantity * currentValue.product.price);
            }, 0);
            const discountedTotal = itemsDiscountedPrices.reduce((accumulator, currentValue, index) => {
                const price = getDiscountedPrice(currentValue.discountedPrice, cartItems[index].product.price);
                return accumulator + (cartItems[index].quantity * price);
            }, 0);
            const decimalPlaces = 2;
            return (cartTotal - discountedTotal).toFixed(decimalPlaces);
        }

        function updateDiscountedPrices(cartItems, currentDiscountedPrices) {
            return cartItems.map((item) => {
                const quantity = item.quantity;
                const productVariantId = item.product.productVariantId || item.product.productVariants[0].id;
                const existingPriceItem = find(currentDiscountedPrices, (discountedPricesItem) => {
                    return discountedPricesItem.productVariantId === productVariantId;
                });
                const discountedPrice = (existingPriceItem) ? existingPriceItem.discountedPrice : null;
                const itemTotal = (discountedPrice || discountedPrice === 0) ?
                    discountedPrice * quantity : item.product.price * quantity;

                return {
                    discountedPrice,
                    itemTotal,
                    quantity,
                    productVariantId,
                    itemPrice: item.product.price
                };
            });
        }

        function getDiscountedPrice(price, fallback) {
            return price === 0 ? price : price || fallback;
        }

        function hasDiscountedPrices(discountedPrices) {
            const discountedItem = find(discountedPrices, (item) => {
                return item.discountedPrice !== null;
            });
            return !!discountedItem;
        }

        function getDiscountedItemTotals(cartItems, itemDiscountedPrices) {
            const discountedPrices = cloneDeep(itemDiscountedPrices);
            return discountedPrices.map((item, index) => {
                const price = getDiscountedPrice(item.discountedPrice, cartItems[index].product.price);
                item.itemTotal = price * cartItems[index].quantity;
                return item;
            });
        }

        function basicDialogController(scopeVars) {
            return function DialogController($scope, $mdDialog) { // eslint-disable-line no-shadow
                'ngInject'; // eslint-disable-line lines-around-directive

                assign($scope, scopeVars);

                $scope.closeDialog = function() {
                    $mdDialog.hide();
                };
            };
        }

        function createOrUpdateCustomDiscount({
            code = uuid().toUpperCase(),
            customDiscountType,
            customDiscountAmount,
            currentDiscount
        } = {}) {
            return launchDarklyService.getFeature('new-admin-features-enabled')
                .then((launchDarklyResponse) => {
                    const newDiscountsEnabled = launchDarklyResponse.includes('discounts');

                    const customDiscountTypes = {
                        PERCENT: 'percent',
                        FIXED: 'fixed'
                    };

                    if (newDiscountsEnabled) {
                        const discountType = customDiscountType ===
                            customDiscountTypes.PERCENT ? customDiscountTypes.PERCENT : customDiscountTypes.FIXED;
                        const discountAmount = Math.round(Number.parseFloat(customDiscountAmount) * 100);

                        if (currentDiscount) {
                            return discountsRESTService.updateDiscount(
                                currentDiscount, discountAmount
                            );
                        }

                        let discountREST = {
                            discountStatus: 'active',
                            discountClass: discountType,
                            discountCode: code,
                            startAt: new Date(Date.now() - 60000),
                            name: 'Custom',
                            requirements: {
                                fakeId: {
                                    requirementType: 'uses',
                                    requirementScopes: {
                                        fakeId: {
                                            entityType: 'storewide'
                                        }
                                    },
                                    boundary: {
                                        boundaryType: 'quantity',
                                        boundaryOperator: '$lte',
                                        boundaryValue: 1
                                    }
                                }
                            },
                            targets: {
                                fakeId: {
                                    targetScopes: {
                                        fakeId: {
                                            entityType: 'subtotal'
                                        }
                                    },
                                    discountAmount,
                                    discountType
                                }
                            }
                        };
                        return discountsRESTService.create(discountREST);
                    }

                    let fixedAmount = 0;
                    let percentageAmount = 0;
                    if (customDiscountType === customDiscountTypes.PERCENT) {
                        percentageAmount = Number.parseFloat(customDiscountAmount);
                    } else {
                        fixedAmount = Number.parseFloat(customDiscountAmount);
                    }

                    let discount = {
                        code,
                        isActive: true,
                        fixedPriceReduction: fixedAmount,
                        percentagePriceReduction: percentageAmount,
                        discountType: customDiscountType,
                        unlimited: true,
                        unlimitedDuration: true,
                        hasStartDateTime: false,
                        hasEndDateTime: false,
                        currentUsageCount: 0,
                        totalUsageCount: 0,
                        isHidden: true,
                        scope: {
                            entireStore: true
                        },
                        name: 'Custom'
                    };

                    return discountsService.create(discount);
                });
        }

        function tabulateAndCreateCustomDiscount(cartItems, discountedPrices, currentDiscount) {
            const totalDiscount = getCombinedItemsDiscount(cartItems, discountedPrices);

            const customDiscountType = 'fixed';
            const discountOptions = {
                customDiscountType,
                customDiscountAmount: totalDiscount,
                currentDiscount
            };
            return createOrUpdateCustomDiscount(discountOptions)
                .then((discountResponse) => {
                    return {
                        discountCode: discountResponse.code,
                        discountType: 'custom',
                        discount: discountResponse.discount
                    };
                });
        }

        function isInventoryTracked(product) {
            return getByPath(product, 'productVariants[0].isInventoryTracked', false);
        }

        function isInvalidQuantity(order, index) {
            const qty = getItemQuantity(order, index);
            return (!qty || !isInteger(qty));
        }

        function isQuantityExceedingInventory(params) {
            const { order, index, product, initialSkuQuantity } = params;
            const qty = getItemQuantity(order, index);
            if (isInventoryTracked(product)) {
                const numberAvailable = getQuantityMoreAvailable(product, qty, initialSkuQuantity);
                if (numberAvailable < 0) {
                    return true;
                }
            }
            return false;
        }

        function getItemQuantity(order, index) {
            return getByPath(order, `cart.items[${index}].quantity`);
        }

        function getQuantityMoreAvailable(product, quantity, initialSkuQuantity) {
            const initialQuantity = getByPath(initialSkuQuantity, product.sku, 0);
            return (getProductInventory(product) + initialQuantity) - quantity;
        }

        function getProductInventory(product) {
            return getByPath(product, 'productVariants[0].quantity', 0);
        }

        function parseCartError(errorResponse) {
            const message = {
                title: 'Problem editing order',
                body: '<p>Please try again later</p>'
            };
            const responseMessage = getByPath(errorResponse, 'data.message');
            if (responseMessage && startsWith(responseMessage, MONGO_ERROR)) {
                try {
                    const messageObj = JSON.parse(responseMessage.replace(MONGO_ERROR, ''));
                    const code = messageObj['criteria.couponCode'];
                    const formattedError = `<p>The discount ${code} has been deleted</p>`;
                    const nextStep = '<p><a href="/discounts/add">Add a new discount</a> with the same code.</p>';
                    message.body = `${formattedError}${nextStep}`;
                } catch (err) {
                    console.log('parseCartError:', err); // eslint-disable-line no-console
                }
                return message;
            }
            if (responseMessage) {
                message.body = `<p>${responseMessage}</p>`;
            }
            return message;
        }

        function mergeOrder(orderWithChargeChanges, orderWithItems) {
            const order = cloneDeep(orderWithItems);
            const chargeProps = pick(orderWithChargeChanges,
                [
                    'authorizedAmount',
                    'authorizeNetTransactionId',
                    'isRefunded',
                    'paymentLifecycle',
                    'refundedAmount',
                    'revision',
                    'stripeRefunds',
                    'stripeEvents'
                ]
            );
            assign(order, chargeProps);
            return order;
        }

        function showErrorModal(error) {
            return $translate([
                'ERROR.GENERAL_FAILURE_ERROR',
                'ERROR.GENERAL_OK_BUTTON',
                'ERROR.INVALID_PRODUCT'
            ]).then((translations) => {
                const errorMessage = getErrorMessage(error, translations);
                return $mdDialog.show(
                    $mdDialog.alert()
                        .title('Error')
                        .textContent(errorMessage)
                        .ariaLabel(errorMessage)
                        .ok(translations['ERROR.GENERAL_OK_BUTTON'])
                        .clickOutsideToClose(true)
                );
            });
        }

        function getErrorMessage(error, translations) {
            let errmsg = translations['ERROR.GENERAL_FAILURE_ERROR'];
            const errItem = getErrItem(error);
            if (errItem) {
                errmsg = errItem.message;
                if (isPrematureRefundError(errItem)) {
                    errmsg = `Sorry, could not issue refund yet - the original charge is still processing.
                        This may take up to 24 hours after payment has been accepted.
                        Please wait 24 hours and edit the order again.`;
                }
            } else if (error.data && error.data.message) {
                if (error.data.message.includes('"sku" must be an object')) {
                    errmsg = translations['ERROR.INVALID_PRODUCT'];
                } else if (error.data.message.includes('User authentication failed due to invalid authentication')) {
                    // general Auth.net CC tokenization failure - likely a problem with Auth.net's constants
                    errmsg = `Authorize.net failed to encrypt the credit card. There may be an issue
                        with Authorize.net's configuration with Volusion Payments. Please contact customer support.`;
                } else if (error.data.message.includes('"postalCode" is required, path: postalCode')) {
                    errmsg = 'An address with a zipcode is required.';
                } else {
                    errmsg = error.data.message;
                }
            }
            return errmsg;
        }

        function getErrItem(error) {
            return getByPath(error, 'data.items[0]', null);
        }

        function isPrematureRefundError(errItem) {
            return !!(errItem && errItem.message === 'Failed to refund transaction.' &&
                errItem.additionalErrorData && errItem.additionalErrorData.length > 1 &&
                errItem.additionalErrorData[0].name === 'AuthorizeDotNetCode' &&
                errItem.additionalErrorData[0].value === 'E00027' &&
                errItem.additionalErrorData[1].name === 'AuthorizeDotNetMessage' &&
                errItem.additionalErrorData[1].value === 'The transaction was unsuccessful.'
            );
        }

        function getRequestId() {
            const REQUEST_ID_CHAR_LIMIT = 20;
            return uuid().replace(/-/g, '').substring(0, REQUEST_ID_CHAR_LIMIT);
        }

        function isFullyRefunded(order) {
            const grandTotal = getByPath(order, 'grandTotal', 0);
            const authorizedAmount = getByPath(order, 'authorizedAmount', grandTotal);

            if (!authorizedAmount) {
                return false;
            }

            return (authorizedAmount - order.refundedAmount) === 0;
        }

        function getAdjustmentAmount(order) {
            if (!getByPath(order, 'paymentLifecycle')) return 0;
            const toAdjustment = (event) => {
                if (event.eventType === 'AdjustmentCharge') return event.chargeAmount;

                return 0;
            };
            const add = (acc, val) => { return acc + val; };

            return order.paymentLifecycle.map(toAdjustment).reduce(add, 0);
        }

        /*
        * We have a property called refundedFromOrder set to true for
        * orders refunded by choosing the Mark Order as Returned. Only those
        * refunds should be substracted from the grandTotal.
        */
        function getRefund(order, refundedFromOrder = false) {
            if (!getByPath(order, 'paymentLifecycle')) return 0;
            const toAdjustment = (event) => {
                if (event.eventType === 'refund' &&
                 event.refundedFromOrder === refundedFromOrder) return event.refundAmount;

                return 0;
            };
            const add = (acc, val) => { return acc + val; };

            return order.paymentLifecycle.map(toAdjustment).reduce(add, 0);
        }

        function getRefundFromOrderCancelled(order) {
            return getRefund(order, true);
        }

        function getRefundFromProductUpdates(order) {
            return getRefund(order);
        }

        function getAdjustedGrandTotal(order) {
            return isFullyRefunded(order) ? 0 : order.cart.grandTotal - getRefundFromOrderCancelled(order);
        }

        function getOriginalAmount(order) {
            const total = order.grandTotal || order.cart.grandTotal;
            return (total - getAdjustmentAmount(order)) + getRefundFromProductUpdates(order);
        }

        function isTestOrder(order) {
            return order.authorizeNetTransactionId === '0';
        }

        function getNextOrderId(order, filter, sort) {
            const [sortByField, sortDirection] = sort.split(' ');
            const filterBy = filter && buildFilter(filter);
            const comparator = sortDirection === 'desc' ? 'lt' : 'gt';
            let value = order[sortByField];

            if (sortByField === 'status') value = `"${value}"`;
            if (sortByField === 'grandTotal') value *= 100;

            let nextFilter = `
                ((${sortByField} ${comparator} ${value}) or
                (
                    ${sortByField} eq ${value} and
                    id ${sortDirection === 'asc' ? 'gt' : 'lt'} "${order.id}"
                ))
            `;

            if (filterBy) nextFilter = `(${filterBy}) and ${nextFilter}`;

            const sortBy = `${sortByField} ${sortDirection}, id ${sortDirection}`;
            return dataService.getAll(settings.api.orders, {
                filter: nextFilter.replace(/\s+/g, ' ').replace(/\n/g, '').trim(),
                sortBy,
                pageSize: 1
            }).then(o => getByPath(o.items, '[0].id', null));
        }

        function getPreviousOrderId(order, filter, sort) {
            const [sortByField, sortDirection] = sort.split(' ');
            const reverseSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            const filterBy = filter && buildFilter(filter);
            const comparator = sortDirection === 'desc' ? 'gt' : 'lt';
            let value = order[sortByField];

            if (sortByField === 'status') value = `"${value}"`;
            if (sortByField === 'grandTotal') value *= 100;

            let previousFilter = `
                ((${sortByField} ${comparator} ${value}) or
                (
                    ${sortByField} eq ${value} and
                    id ${sortDirection === 'asc' ? 'lt' : 'gt'} "${order.id}"
                ))
            `;

            if (filterBy) previousFilter = `(${filterBy}) and ${previousFilter}`;

            const sortBy = `${sortByField} ${reverseSortDirection}, id ${reverseSortDirection}`;
            return dataService.getAll(settings.api.orders, {
                filter: previousFilter.replace(/\s+/g, ' ').replace(/\n/g, '').trim(),
                sortBy,
                pageSize: 1
            }).then(o => getByPath(o.items, '[0].id', null));
        }

        function resendOrderConfirmationEmail(id) {
            return dataService.create(`${settings.api.orders}/${id}/sendEmail`, {
                type: 'orderConfirmation'
            });
        }

        function resendShippingConfirmationEmail(id) {
            return dataService.create(`${settings.api.orders}/${id}/sendEmail`, {
                type: 'shippingConfirmation'
            });
        }

        function isRefundWaitingPeriodPassed(order) {
            const waitingPeriodInHours = 24;
            return moment().diff(order.createdOn, 'hours') >= waitingPeriodInHours;
        }

        function handleTaxProviderMessageEvent(event, actionString) {
            if (event.taxProviderMessage) {
                const taxProviderMessage =
                    event.taxProviderMessage.slice(0, 1).toLowerCase()
                    + event.taxProviderMessage.slice(1);

                const modalContent =
                    `
                    The ${actionString} was successful. Unfortunately, 
                    ${taxProviderMessage}
                    Please contact Avalara for further assistance.
                    `;
                return $mdDialog.show(
                    $mdDialog.alert()
                        .title('Avalara Notice')
                        .textContent(modalContent)
                        .ariaLabel(modalContent)
                        .ok('OK')
                        .clickOutsideToClose(true)
                );
            }

            return $q.resolve();
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/orders.service.js